import React from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  Grid,
  Paper,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { BOARD_TYPES, BOARD_TYPE_TITLES } from './CreateBoardModal';

/**
 * Modal para seleccionar el tipo de tablero a crear
 * Sigue un diseño minimalista y formal
 */
const SelectBoardTypeModal = ({ open, onClose, onSelectType }) => {
  const theme = useTheme();

  // Definir las opciones de tipo de tablero con sus detalles
  const boardTypeOptions = [
    {
      type: BOARD_TYPES.GENERAL,
      title: BOARD_TYPE_TITLES[BOARD_TYPES.GENERAL],
      description: 'Para organizar actividades generales.',
      icon: <DashboardIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />,
      disabled: false
    },
    {
      type: BOARD_TYPES.SOPORTE,
      title: BOARD_TYPE_TITLES[BOARD_TYPES.SOPORTE],
      description: 'Para gestionar casos de soporte y atención al cliente.',
      icon: <SupportAgentIcon sx={{ fontSize: 32, color: theme.palette.secondary.main }} />,
      disabled: false
    }
  ];

  // Manejar la selección de un tipo
  const handleSelect = (type, disabled) => {
    // Si la opción está deshabilitada, no hacer nada
    if (disabled) {
      return;
    }

    // Si no está deshabilitada, proceder normalmente
    onSelectType(type);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-select-board-type"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600, // Reducimos el ancho para dos opciones
        maxWidth: '90vw',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        p: 0,
        overflow: 'hidden'
      }}>
        {/* Encabezado */}
        <Box
          sx={{
            p: 2,
            backgroundColor: theme.palette.background.default,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography
            id="modal-select-board-type"
            variant="h5"
            component="h2"
            sx={{ fontWeight: 500 }}
          >
            Seleccionar tipo de tablero
          </Typography>
          <IconButton onClick={onClose} aria-label="cerrar" size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contenido: Opciones de tipo de tablero - Centradas */}
        <Box sx={{ p: 4, py: 5 }}>
          <Grid container spacing={3} justifyContent="center">
            {boardTypeOptions.map((option) => (
              <Grid item xs={12} sm={6} key={option.type}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    height: '100%',
                    minHeight: 220, // Altura mínima para mantener consistencia
                    cursor: option.disabled ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: option.disabled ? 0.7 : 1,
                    '&:hover': {
                      transform: option.disabled ? 'none' : 'translateY(-4px)',
                      boxShadow: option.disabled ? 'none' : theme.shadows[3],
                      borderColor: option.disabled ? theme.palette.grey[300] : theme.palette.primary.main
                    },
                    borderRadius: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center', // Centrar verticalmente el contenido
                    textAlign: 'center',
                    position: 'relative',
                    mx: 'auto', // Margen automático horizontal
                    maxWidth: 280 // Ancho máximo para cada tarjeta
                  }}
                  onClick={() => handleSelect(option.type, option.disabled)}
                >
                  {/* Icono */}
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    mb: 3, // Aumentamos el margen inferior
                    borderRadius: '50%',
                    backgroundColor: theme.palette.background.default,
                    width: 80, // Aumentamos el tamaño
                    height: 80 // Aumentamos el tamaño
                  }}>
                    {React.cloneElement(option.icon, { sx: { fontSize: 40 } })} {/* Icono más grande */}
                  </Box>

                  {/* Texto */}
                  <Box>
                    <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600, fontSize: '1.25rem' }} component="div">
                      {option.title}
                      {option.disabled && (
                        <Typography
                          component="span"
                          sx={{
                            ml: 1,
                            fontSize: '0.75rem',
                            color: 'warning.main',
                            fontWeight: 'bold',
                            bgcolor: 'warning.light',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1
                          }}
                        >
                          PRÓXIMAMENTE
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ px: 2 }}>
                      {option.description}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Pie del modal */}
        <Box sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}`, textAlign: 'right' }}>
          <Button onClick={onClose} variant="outlined">
            Cancelar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default SelectBoardTypeModal;